import { useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import PropTypes from 'prop-types'
import ReactTooltip from 'react-tooltip'
import colors from '../../../assets/styles/globals.scss'
import moment from 'moment'

import { DATETIME_FORMAT, getMomentInCommunityTimezoneReversed } from '../../../utility/datesHelper'
import { yardageByLevel } from '../../../utility/curriculumHelper'

import { ReactComponent as AccountIcon } from '../../../assets/images/common/icons/sidebarMenu/Account.svg'
import { ReactComponent as AccountLetterIcon } from '../../../assets/images/common/icons/account-letter-icon.svg'
import { ReactComponent as AccountFlagIcon } from '../../../assets/images/common/icons/account-flag-icon.svg'
import { ReactComponent as GolfJournalIcon } from '../../../assets/images/common/icons/golf-journal-icon.svg'
import { ReactComponent as CalendarIcon } from '../../../assets/images/common/icons/calendar-icon-v2.svg'

import classes from './ReportsPage.module.scss'
import classNames from 'classnames'
import DownloadButton from './Reports/DownloadButton'
import PreviewModal from './Reports/PreviewModal'

const generateCSVData = (pairings, timezone, type) => {
  const csvData = pairings.pairingGroups.flatMap((group) => (
    group.users?.filter(Boolean).map((student) => ({
      time: getMomentInCommunityTimezoneReversed(moment(group.startTime), timezone, DATETIME_FORMAT).format('LT'),
      pairingGroup: group.order,
      hole: pairings.format === 'Shotgun'
        ? `${group.startHole?.value || ''} ${group.playersGroup?.value || ''}` : '',
      firstName: student.firstName,
      lastName: student.lastName,
      level: student.level,
      holeDistance: yardageByLevel[student.level] / 9,
      totalYardage: yardageByLevel[student.level],
      playerNotes: student.enrollment.note,
    })) || []
  ))

  if (type === 'PairingsAlphaTeeSheet') {
    csvData.sort((a, b) =>
      a.lastName?.toLowerCase().localeCompare(b.lastName?.toLowerCase())
    )
  }

  return csvData
}

const ReportsPage = ({ pairingsCreated }) => {
  const { t } = useTranslation()
  const timezone = useSelector(state => state.user.current_user.communities[0].timezone)
  const event = useSelector(state => state.nineHoleEvent)

  const csvHeaders = [
    { label: t('events.common.time'), key: 'time' },
    { label: t('events.reports.csv.pairingGroup'), key: 'pairingGroup' },
    { label: t('events.common.hole'), key: 'hole' },
    { label: t('common.field.firstName'), key: 'firstName' },
    { label: t('common.field.lastName'), key: 'lastName' },
    { label: t('events.common.level'), key: 'level' },
    { label: t('events.reports.csv.holeDistance'), key: 'holeDistance' },
    { label: t('events.reports.csv.totalYardage'), key: 'totalYardage' },
    { label: t('events.reports.csv.playerNotes'), key: 'playerNotes' },
  ]

  const [selectedReport, setSelectedReport] = useState(null)
  const [printOptions, setPrintOptions] = useState({})
  const [showPreviewModal, setShowPreviewModal] = useState(false)
  const [data, setData] = useState([])

  const handleDownloadReport = (type) => {
    setSelectedReport(type)
    setData(generateCSVData(event.pairings, timezone, type))
  }

  const downloadPDF = (type, printOptions) => {
    setSelectedReport(type)
    setPrintOptions(printOptions)
    setShowPreviewModal(true)
  }

  const refs = useRef({})
  const handleMouseEnter = id => {
    if (refs.current[id]) {
      ReactTooltip.show(refs.current[id])
    }
  }

  const handleMouseLeave = id => {
    if (refs.current[id]) {
      ReactTooltip.hide(refs.current[id])
    }
  }

  return (
    <div className={classes.eventReportsPage}>
      <ReactTooltip
        type='light'
        effect='solid'
        arrowColor={colors.placeholderGray}
        className={classNames(classes.tooltip)}
        offset={{ top: -5 }}
      />
      <div className='w-75 mx-auto'>
        <div className={classes.reportWrapper}
          onMouseEnter={() => handleMouseEnter(1)}
          onMouseLeave={() => handleMouseLeave(1)}
        >
          <div className={classes.reportInfo}>
            <AccountIcon className={classes.reportIcon}/>
            <div>
              <p className={classes.reportTitle}>{t('events.reports.pairingsTeeSheet.title')}</p>
              <p className={classes.reportDescription}>
                {t('events.reports.pairingsTeeSheet.description')}
              </p>
            </div>
          </div>
          <div
            className={classes.downloadButtonWrapper}
            ref={el => (refs.current[1] = el)}
            data-tip={!pairingsCreated ? t('events.reports.pairingsRequiredTooltip') : null}
          >
            <DownloadButton
              multiaction
              disabled={!pairingsCreated}
              downloadPDF={() => downloadPDF('PairingsTeeSheet')}
              onClick={() => handleDownloadReport('PairingsTeeSheet')}
              csv={{ headers: csvHeaders, data: data }}
              type='PairingsTeeSheet'
              eventName={event.name}
            />
          </div>
        </div>
        <div className={classes.reportWrapper}
          onMouseEnter={() => handleMouseEnter(2)}
          onMouseLeave={() => handleMouseLeave(2)}
        >
          <div className={classes.reportInfo}>
            <AccountLetterIcon className={classes.reportIcon}/>
            <div>
              <p className={classes.reportTitle}>{t('events.reports.alphaSheet.title')}</p>
              <p className={classes.reportDescription}>
                {t('events.reports.alphaSheet.description')}
              </p>
            </div>
          </div>
          <div
            className={classes.downloadButtonWrapper}
            ref={el => (refs.current[2] = el)}
            data-tip={!pairingsCreated ? t('events.reports.pairingsRequiredTooltip') : null}
          >
            <DownloadButton
              multiaction
              disabled={!pairingsCreated}
              downloadPDF={() => downloadPDF('PairingsAlphaTeeSheet')}
              onClick={() => handleDownloadReport('PairingsAlphaTeeSheet')}
              csv={{ headers: csvHeaders, data: data }}
              type='PairingsAlphaTeeSheet'
              eventName={event.name}
            />
          </div>
        </div>
        <div className={classes.reportWrapper}
          onMouseEnter={() => handleMouseEnter(3)}
          onMouseLeave={() => handleMouseLeave(3)}
        >
          <div className={classes.reportInfo}>
            <CalendarIcon className={classes.reportIcon}/>
            <div>
              <p className={classes.reportTitle}>{t('events.reports.cartSigns.title')}</p>
              <p className={classes.reportDescription}>
                {t('events.reports.cartSigns.description')}
              </p>
            </div>
          </div>
          <div
            className={classes.downloadButtonWrapper}
            ref={el => (refs.current[3] = el)}
            data-tip={!pairingsCreated ? t('events.reports.pairingsRequiredTooltip') : null}
          >
            <DownloadButton
              disabled={!pairingsCreated}
              onClick={() => downloadPDF('CartSigns')}
              type='CartSigns'
            />
          </div>
        </div>
        <div className={classes.reportWrapper}
          onMouseEnter={() => handleMouseEnter(4)}
          onMouseLeave={() => handleMouseLeave(4)}
        >
          <div className={classes.reportInfo}>
            <GolfJournalIcon className={classes.reportIcon}/>
            <div>
              <p className={classes.reportTitle}>{t('events.reports.scorecards.title')}</p>
              <p className={classes.reportDescription}>
                {t('events.reports.scorecards.description')}
              </p>
            </div>
          </div>
          <div
            className={classes.downloadButtonWrapper}
            ref={el => (refs.current[4] = el)}
            data-tip={!pairingsCreated ? t('events.reports.pairingsRequiredTooltip') : null}
          >
            <DownloadButton
              disabled={!pairingsCreated}
              downloadPDF={(printOptions) => downloadPDF('ScoreCards', { ...printOptions, orientation: 'l' })}
              onClick={() => handleDownloadReport('ScoreCards')}
              type='ScoreCards'
            />
          </div>
        </div>
        <div className={classes.reportWrapper}
          onMouseEnter={() => handleMouseEnter(5)}
          onMouseLeave={() => handleMouseLeave(5)}
        >
          <div className={classes.reportInfo}>
            <AccountFlagIcon className={classes.reportIcon}/>
            <div>
              <p className={classes.reportTitle}>{t('events.reports.scoreboards.title')}</p>
              <p className={classes.reportDescription}>
                {t('events.reports.scoreboards.description')}
              </p>
            </div>
          </div>
          <div
            className={classes.downloadButtonWrapper}
            ref={el => (refs.current[4] = el)}
          >
            <DownloadButton
              onClick={() => downloadPDF('ScoreBoards', { orientation: 'l' })}
              type='ScoreBoards'
            />
          </div>
        </div>
      </div>
      <PreviewModal
        showModal={showPreviewModal === true}
        closeModal={() => setShowPreviewModal(false)}
        report={selectedReport}
        printOptions={printOptions}
        event={event}
      />

    </div>
  )
}

ReportsPage.propTypes = {
  completed: PropTypes.bool,
  pairingsCreated: PropTypes.bool,
}

export default ReportsPage
